import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  ArrowLeft, Plus, Save, Trash2, GripVertical, Type,
  AlignLeft, CheckSquare, List, Hash, Calendar, FileText
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_TYPES = [
  { value: 'section_header', label: 'Section Header', icon: <Type size={16} />, description: 'Large heading to organize sections' },
  { value: 'text', label: 'Short Text', icon: <AlignLeft size={16} />, description: 'Single line text input' },
  { value: 'textarea', label: 'Long Text', icon: <FileText size={16} />, description: 'Multi-line text area' },
  { value: 'yes_no', label: 'Yes/No', icon: <CheckSquare size={16} />, description: 'Boolean yes/no question' },
  { value: 'checkbox', label: 'Checkbox', icon: <CheckSquare size={16} />, description: 'Single checkbox' },
  { value: 'number', label: 'Number', icon: <Hash size={16} />, description: 'Numeric input' },
  { value: 'date', label: 'Date', icon: <Calendar size={16} />, description: 'Date picker' },
  { value: 'dropdown', label: 'Dropdown', icon: <List size={16} />, description: 'Select from options' },
];

const QCSheetTemplateBuilder = () => {
  const navigate = useNavigate();
  const { sheetType } = useParams();
  const [template, setTemplate] = useState(null);
  const [fields, setFields] = useState([]);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldForm, setFieldForm] = useState({
    type: 'text',
    label: '',
    description: '',
    required: false,
    options: ''
  });

  const loadTemplate = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/qc-sheets/templates/${sheetType}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplate(response.data);
      setFields(response.data.fields || []);
    } catch (error) {
      console.error('Failed to load template:', error);
      // If template doesn't exist yet, create basic one
      const templatesRes = await axios.get(`${API}/qc-sheets/templates`, {
        headers: { Authorization: localStorage.getItem('token') }
      });
      const templateInfo = templatesRes.data.templates.find(t => t.sheet_type === sheetType);
      if (templateInfo) {
        setTemplate({
          sheet_type: sheetType,
          name: templateInfo.name,
          description: templateInfo.description,
          category: templateInfo.category,
          fields: []
        });
        setFields([]);
      }
    }
  }, [sheetType]);

  useEffect(() => {
    loadTemplate();
  }, [loadTemplate]);

  const handleAddField = () => {
    setEditingField(null);
    setFieldForm({
      type: 'text',
      label: '',
      description: '',
      required: false,
      options: ''
    });
    setShowFieldModal(true);
  };

  const handleEditField = (index) => {
    const field = fields[index];
    setEditingField(index);
    setFieldForm({
      type: field.type,
      label: field.label,
      description: field.description || '',
      required: field.required || false,
      options: field.options ? field.options.join('\n') : ''
    });
    setShowFieldModal(true);
  };

  const handleSaveField = () => {
    const newField = {
      type: fieldForm.type,
      label: fieldForm.label,
      description: fieldForm.description,
      required: fieldForm.required,
      options: fieldForm.options ? fieldForm.options.split('\n').filter(o => o.trim()) : []
    };

    if (editingField !== null) {
      const updatedFields = [...fields];
      updatedFields[editingField] = newField;
      setFields(updatedFields);
    } else {
      setFields([...fields, newField]);
    }

    setShowFieldModal(false);
    toast.success('Field saved');
  };

  const handleDeleteField = (index) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      const updatedFields = fields.filter((_, i) => i !== index);
      setFields(updatedFields);
      toast.success('Field deleted');
    }
  };

  const handleMoveField = (index, direction) => {
    const newFields = [...fields];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    
    [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]];
    setFields(newFields);
  };

  const handleSaveTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/qc-sheets/templates/${sheetType}`,
        { fields },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Template saved successfully');
      navigate('/tools/qc-sheets');
    } catch (error) {
      console.error('Failed to save template:', error);
      toast.error('Failed to save template');
    }
  };

  const getFieldIcon = (type) => {
    const fieldType = FIELD_TYPES.find(ft => ft.value === type);
    return fieldType ? fieldType.icon : <Type size={16} />;
  };

  if (!template) {
    return (
      <div className="p-6">
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            Loading template...
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-4 mb-2">
            <Button
              onClick={() => navigate('/tools/qc-sheets')}
              variant="outline"
              size="sm"
            >
              <ArrowLeft size={16} className="mr-2" />
              Back to QC Sheets
            </Button>
          </div>
          <h1 className="text-3xl font-black tracking-tight uppercase">Form Builder</h1>
          <p className="text-muted-foreground mt-1">{template.name}</p>
        </div>
        <Button onClick={handleSaveTemplate}>
          <Save size={16} className="mr-2" />
          Save Template
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Form Fields */}
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Form Fields</CardTitle>
                  <CardDescription>
                    Define the fields that will appear in this QC sheet
                  </CardDescription>
                </div>
                <Button onClick={handleAddField}>
                  <Plus size={16} className="mr-2" />
                  Add Field
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {fields.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText size={48} className="mx-auto mb-3 opacity-50" />
                  <p>No fields added yet</p>
                  <Button variant="outline" size="sm" className="mt-3" onClick={handleAddField}>
                    <Plus size={14} className="mr-2" />
                    Add Your First Field
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  {fields.map((field, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-3 p-3 border rounded-lg hover:border-primary transition-all"
                    >
                      <div className="flex flex-col gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleMoveField(index, 'up')}
                          disabled={index === 0}
                        >
                          <GripVertical size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={() => handleMoveField(index, 'down')}
                          disabled={index === fields.length - 1}
                        >
                          <GripVertical size={14} />
                        </Button>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-1">
                        {getFieldIcon(field.type)}
                        <div className="flex-1">
                          <p className="font-medium text-sm">{field.label}</p>
                          <p className="text-xs text-muted-foreground">
                            {FIELD_TYPES.find(ft => ft.value === field.type)?.label || field.type}
                            {field.required && ' • Required'}
                          </p>
                        </div>
                      </div>

                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEditField(index)}
                        >
                          <Type size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteField(index)}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Preview */}
          <Card>
            <CardHeader>
              <CardTitle>Preview</CardTitle>
              <CardDescription>
                How the form will look when filling out the sheet
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {fields.map((field, index) => (
                  <FormFieldPreview key={index} field={field} />
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Template Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <p className="text-muted-foreground">Type</p>
                <p className="font-medium">{template.sheet_type}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Category</p>
                <p className="font-medium capitalize">{template.category}</p>
              </div>
              <div>
                <p className="text-muted-foreground">Fields Count</p>
                <p className="font-medium">{fields.length}</p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Field Types</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {FIELD_TYPES.map((fieldType) => (
                  <div key={fieldType.value} className="flex items-start gap-2 text-sm">
                    {fieldType.icon}
                    <div>
                      <p className="font-medium">{fieldType.label}</p>
                      <p className="text-xs text-muted-foreground">{fieldType.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Add/Edit Field Modal */}
      <Dialog open={showFieldModal} onOpenChange={setShowFieldModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingField !== null ? 'Edit Field' : 'Add Field'}
            </DialogTitle>
            <DialogDescription>
              Configure the field properties
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="fieldType">Field Type *</Label>
              <Select value={fieldForm.type} onValueChange={(value) => setFieldForm({ ...fieldForm, type: value })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((ft) => (
                    <SelectItem key={ft.value} value={ft.value}>
                      <div className="flex items-center gap-2">
                        {ft.icon}
                        <span>{ft.label}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="label">Label *</Label>
              <Input
                id="label"
                value={fieldForm.label}
                onChange={(e) => setFieldForm({ ...fieldForm, label: e.target.value })}
                placeholder="e.g., Weld Visual Inspection"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description (optional)</Label>
              <Textarea
                id="description"
                value={fieldForm.description}
                onChange={(e) => setFieldForm({ ...fieldForm, description: e.target.value })}
                placeholder="Additional instructions or help text..."
                rows={2}
              />
            </div>

            {fieldForm.type === 'dropdown' && (
              <div className="space-y-2">
                <Label htmlFor="options">Options (one per line) *</Label>
                <Textarea
                  id="options"
                  value={fieldForm.options}
                  onChange={(e) => setFieldForm({ ...fieldForm, options: e.target.value })}
                  placeholder="Option 1&#10;Option 2&#10;Option 3"
                  rows={4}
                />
              </div>
            )}

            {fieldForm.type !== 'section_header' && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="required"
                  checked={fieldForm.required}
                  onChange={(e) => setFieldForm({ ...fieldForm, required: e.target.checked })}
                  className="rounded"
                />
                <Label htmlFor="required" className="font-normal">
                  Required field
                </Label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFieldModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveField} disabled={!fieldForm.label}>
              {editingField !== null ? 'Update Field' : 'Add Field'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Form Field Preview Component
const FormFieldPreview = ({ field }) => {
  switch (field.type) {
    case 'section_header':
      return (
        <div className="pt-4 pb-2">
          <h3 className="text-lg font-bold">{field.label}</h3>
          {field.description && (
            <p className="text-sm text-muted-foreground mt-1">{field.description}</p>
          )}
        </div>
      );
    
    case 'text':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <Input placeholder={`Enter ${field.label.toLowerCase()}...`} disabled />
        </div>
      );
    
    case 'textarea':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <Textarea placeholder={`Enter ${field.label.toLowerCase()}...`} rows={3} disabled />
        </div>
      );
    
    case 'yes_no':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <div className="flex gap-4">
            <label className="flex items-center gap-2">
              <input type="radio" name={`preview-${field.label}`} disabled />
              <span>Yes</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name={`preview-${field.label}`} disabled />
              <span>No</span>
            </label>
          </div>
        </div>
      );
    
    case 'checkbox':
      return (
        <div className="flex items-start gap-2">
          <input type="checkbox" className="mt-1" disabled />
          <div className="flex-1">
            <Label>
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            {field.description && (
              <p className="text-xs text-muted-foreground mt-1">{field.description}</p>
            )}
          </div>
        </div>
      );
    
    case 'number':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <Input type="number" placeholder="0" disabled />
        </div>
      );
    
    case 'date':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <Input type="date" disabled />
        </div>
      );
    
    case 'dropdown':
      return (
        <div className="space-y-2">
          <Label>
            {field.label}
            {field.required && <span className="text-red-500 ml-1">*</span>}
          </Label>
          {field.description && (
            <p className="text-xs text-muted-foreground">{field.description}</p>
          )}
          <Select disabled>
            <SelectTrigger>
              <SelectValue placeholder="Select an option..." />
            </SelectTrigger>
          </Select>
        </div>
      );
    
    default:
      return null;
  }
};

export default QCSheetTemplateBuilder;
